import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { OAuthButtons } from "../components/OAuthButtons";
import { AuthError } from "../components/AuthError";
import { TrustSignals } from "../components/TrustSignals";
import { ValuePropCards } from "../components/ValuePropCards";
import { HowItWorks } from "../components/HowItWorks";
import { HeroImage } from "../components/HeroImage";
import "./SignIn.css";

export const SignIn: React.FC = () => {
  const { user, userProfile, error, clearError } = useAuth();
  const navigate = useNavigate();
  const [signingIn, setSigningIn] = useState(false);

  // Redirect to dashboard if authenticated AND profile is loaded
  useEffect(() => {
    if (user && userProfile) {
      navigate("/dashboard", { replace: true });
    }
  }, [user, userProfile, navigate]);

  const handleDismissError = () => {
    clearError();
  };

  const handleSigningInChange = (isSigningIn: boolean) => {
    setSigningIn(isSigningIn);
  };

  return (
    <div className="signin-container signin-container-split" id="sign-in">
      {/* Full-page signing-in overlay */}
      {signingIn && (
        <div
          className="auth-loading"
          id="auth-loading-overlay"
          aria-live="polite"
        >
          <div className="auth-loading-content">
            <div className="auth-logo">RetireEZ</div>
            <div className="spinner" />
            <p className="loading-text">Signing you in…</p>
          </div>
        </div>
      )}

      {/* Sign-in form — first in DOM for mobile-first layout */}
      <div className="signin-content" id="signin-form">
        <div className="signin-logo">
          <span className="logo-text">RetireEZ</span>
        </div>

        <div className="signin-header">
          <h1 className="signin-title">Plan Your Canadian Retirement</h1>
          <h2 className="signin-subtitle">
            Every Canadian retirement program — one simple plan
          </h2>
        </div>

        <div className="signin-form">
          {error && <AuthError error={error} onDismiss={handleDismissError} />}

          <TrustSignals />

          <OAuthButtons onSigningInChange={handleSigningInChange} />
        </div>

        <div className="signin-legal">
          <p>
            By continuing, you agree to our{" "}
            <Link to="/terms" className="legal-link">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="legal-link">
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>

      {/* Marketing panel — second in DOM, repositioned left on desktop via CSS order */}
      <div className="signin-value-prop" id="signin-value-prop">
        {/* Skip to sign-in link for keyboard/screen-reader users */}
        <a href="#signin-form" className="skip-to-signin sr-only">
          Skip to sign in
        </a>

        <h2 className="signin-hero-headline" id="signin-hero-headline">
          See Your Retirement Timeline in 5 Minutes
        </h2>
        <p className="signin-hero-subheading">
          The only retirement planner built exclusively for Canadians. Try free
          for 7 days — no credit card required.
        </p>

        <HeroImage />

        <ValuePropCards />

        <HowItWorks />
      </div>
    </div>
  );
};

export default SignIn;
